// Opt-in trace of the Web Speech session, for the failure modes that only show
// up against a real microphone and cannot be reproduced in a unit test: the
// recognizer going quiet mid-dictation, a session ending without `onend`, or a
// result landing but never reaching the field.
//
// Off unless someone turns it on, so this is inert in every normal run:
//
//   localStorage.setItem('gable-dictation-debug', '1')   // then dictate
//   JSON.parse(localStorage.getItem('gable-dictation-log'))
//
// The buffer is capped and kept in `localStorage` rather than memory so it
// survives the reload that a page-level fault would otherwise wipe, and can be
// read from a different tab than the one that recorded it. Storage goes
// through the house `LocalService` wrapper (static, so no injector needed).

import { LocalService } from '../../../services/storage/local.service';

const FLAG_KEY = 'gable-dictation-debug';
const LOG_KEY = 'gable-dictation-log';
const MAX_ENTRIES = 200;

export interface DictationDiagEntry {
    /** Milliseconds since the page loaded — deltas are what matter here. */
    t: number;
    event: string;
    [detail: string]: unknown;
}

const enabled = (): boolean => {
    try {
        return LocalService.get(FLAG_KEY) === '1';
    } catch {
        return false;
    }
};

/** Append one entry when tracing is on; a no-op otherwise, never throws. */
export function dictationDiag(event: string, detail: Record<string, unknown> = {}): void {
    if (!enabled()) {
        return;
    }
    try {
        const raw = LocalService.get(LOG_KEY);
        const entries: DictationDiagEntry[] = raw ? JSON.parse(raw) : [];
        entries.push({ t: Math.round(performance.now()), event, ...detail });
        LocalService.set(LOG_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
    } catch {
        /* quota, private mode, or a non-JSON value someone left behind */
    }
}

/** Drop the buffer so the next dictation starts from a clean trace. */
export function clearDictationDiag(): void {
    try {
        LocalService.destroy(LOG_KEY);
    } catch {
        /* nothing to clear */
    }
}
